import { DatabaseService } from '../db/database';
import apiService from './apiService';

class SyncService {
  constructor() {
    this.isSyncing = false;
  }

  // Check if API is available
  async isApiAvailable() {
    try {
      const health = await apiService.healthCheck();
      return health?.status === 'ok';
    } catch (error) {
      return false;
    }
  }

  // Sync local changes to API
  async syncToApi() {
    if (this.isSyncing) {
      console.log('Sync already in progress');
      return { success: false, message: 'Sync already in progress' };
    }

    this.isSyncing = true;

    try {
      // Check if API is available
      const apiAvailable = await this.isApiAvailable();
      if (!apiAvailable) {
        throw new Error('API is not available');
      }

      // Get user info
      const userInfo = await DatabaseService.getUserInfo();
      if (!userInfo.email) {
        throw new Error('User not configured');
      }

      // Ensure user exists on API
      let user = await apiService.getUser(userInfo.email);
      if (!user) {
        user = await apiService.createOrUpdateUser(
          userInfo.name,
          userInfo.email,
          userInfo.countryCode
        );
        await DatabaseService.setUserId(user.id);
      } else if (!userInfo.userId) {
        await DatabaseService.setUserId(user.id);
      }

      const userId = user.id;

      // Get unsynced attendance records
      const unsyncedRecords = await DatabaseService.getUnsyncedAttendance();

      if (unsyncedRecords.length === 0) {
        return {
          success: true,
          message: 'No records to sync',
          count: 0
        };
      }

      // Sync records to API
      const result = await apiService.bulkUpsertAttendance(
        userId,
        unsyncedRecords.map(r => ({
          date: r.date,
          status: r.status
        }))
      );

      // Mark records as synced
      await DatabaseService.markAttendanceSynced(
        unsyncedRecords.map(r => r.date)
      );

      return {
        success: true,
        message: `Synced ${result.count} records`,
        count: result.count
      };
    } catch (error) {
      console.error('Sync to API failed:', error);
      return {
        success: false,
        message: error.message || 'Sync failed',
        error: error
      };
    } finally {
      this.isSyncing = false;
    }
  }

  // Pull data from API to local database
  async syncFromApi() {
    try {
      // Check if API is available
      const apiAvailable = await this.isApiAvailable();
      if (!apiAvailable) {
        throw new Error('API is not available');
      }

      // Get user info
      const userInfo = await DatabaseService.getUserInfo();
      if (!userInfo.email || !userInfo.userId) {
        throw new Error('User not configured');
      }

      // Get current year and previous year
      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear - 1}-01-01`;
      const endDate = `${currentYear}-12-31`;

      // Fetch attendance records from API
      const records = await apiService.getAttendanceRecords(
        userInfo.userId,
        startDate,
        endDate
      );

      // Update local database
      await DatabaseService.bulkSetAttendance(
        records.map(r => ({
          date: r.date,
          status: r.status,
          synced: true,
          updatedAt: r.updated_at
        }))
      );

      return {
        success: true,
        message: `Downloaded ${records.length} records`,
        count: records.length
      };
    } catch (error) {
      console.error('Sync from API failed:', error);
      return {
        success: false,
        message: error.message || 'Download failed',
        error: error
      };
    }
  }

  // Full two-way sync
  async fullSync() {
    const uploadResult = await this.syncToApi();
    if (!uploadResult.success) {
      return uploadResult;
    }

    const downloadResult = await this.syncFromApi();
    return {
      success: downloadResult.success,
      message: `Upload: ${uploadResult.message}, Download: ${downloadResult.message}`,
      uploaded: uploadResult.count,
      downloaded: downloadResult.count
    };
  }
}

const syncServiceInstance = new SyncService();
export default syncServiceInstance;
